import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  ExpandableSection,
  Link,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  InvestigationSuggestion,
  Stage,
} from "../../lusca-search-api/generated-src";
import { OpenInvestigationButton } from "./OpenInvestigationButton";
import { TransactionLink } from "./TransactionLink";
import { CodeProse } from "./CodeProse";
import {
  ownerLabel,
  predicateValueText,
  predicateValues,
} from "./investigationUtils";
import {
  describeConfidence,
  describeFix,
  evidenceSummary,
  layerWord,
  mostlyHeldBy,
  earlierReportLabel,
  earlierReportOf,
  shortPatternId,
  suggestionReportId,
  suggestionHeadline,
} from "./trackstarsText";

export const WHY_PREVIEW_LENGTH = 140;
/** Line-length cap for the card's Why/Fix prose; the headline keeps the full width. */
const READABLE_WIDTH = "110ch";

interface TrackStarsSuggestionCardProps {
  stage: Stage;
  suggestion: InvestigationSuggestion;
  /** Primary action; absent on surfaces that only show the analysis (the case page). */
  onInvestigate?: (suggestion: InvestigationSuggestion) => void;
  /** Extra actions rendered after the built-in ones (the case page adds propose/add-link). */
  actions?: React.ReactNode;
  /** A compact control (the case page's ⋯ menu) shown top-right, where Start/Open sit elsewhere. */
  headerAction?: React.ReactNode;
  /** The report the pattern came from, for the Details link. */
  reportUrl?: string;
  /** Hide the "covered by" line (the case page already is the covering case). */
  hideOwnership?: boolean;
  /** Line under the headline stating how the pattern relates to the case shown. */
  relationNote?: string;
  /**
   * A pattern of the case's own run: titled by its category (as in the run table) and without
   * TrackStars' report-wide match count, which would sit next to this case's count.
   */
  fromRun?: boolean;
}

/** "FIX_IN_PROGRESS" → "Fix in progress". */
export const formatStatus = (status: string): string =>
  status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, " ");

const PRIORITY_COLOR: Record<string, "red" | "blue" | "grey" | "green"> = {
  P1: "red",
  P2: "blue",
  P3: "grey",
  P4: "green",
};

/**
 * One TrackStars pattern as a triage note a person can scan in a few seconds: a headline in the
 * registry's naming, one line on why (the agent's root cause), one line on the fix in plain words,
 * the evidence as counts, and a single Investigate button. TrackStars' full prose, the when-clause,
 * samples and every finding sit behind Details.
 */
export const TrackStarsSuggestionCard: React.FC<
  TrackStarsSuggestionCardProps
> = ({
  stage,
  suggestion: s,
  onInvestigate,
  actions,
  reportUrl,
  hideOwnership,
  relationNote,
  headerAction,
  fromRun,
}) => {
  const [whyExpanded, setWhyExpanded] = useState(false);
  const earlier = earlierReportOf(s);
  // An earlier-report pattern links its own report, not the latest one.
  const detailsReportUrl = earlier ? earlier.reportUrl : reportUrl;
  const why = s.rootCause?.summary?.trim() || s.description?.trim() || "";
  const whyLong = why.length > WHY_PREVIEW_LENGTH;
  const whyShown =
    whyLong && !whyExpanded
      ? `${why.slice(0, WHY_PREVIEW_LENGTH).trimEnd()}…`
      : why;
  // The plain phrase only; TrackStars' own wording of the fix lives in Details.
  const plainFix = describeFix({
    ...s,
    starter: { ...s.starter, fix: undefined },
  });
  const covering = hideOwnership
    ? undefined
    : s.ownership.investigations.find(
        (inv) => inv.relation === "origin" || inv.relation === "covers"
      ) ?? s.ownership.investigations[0];
  // Fully covered: the whole pattern is already someone's case.
  const covered =
    !hideOwnership && s.ownership.state === "owned" ? covering : undefined;
  const canInvestigate = !!s.derivedRule?.length;
  // A newer report found more of a failure an open case is already on: send people to that case.
  const held = !hideOwnership && !covered ? mostlyHeldBy(s) : undefined;
  const shown = held ?? covering;
  const confidenceType =
    s.confidence === "high"
      ? "success"
      : s.confidence === "low"
      ? "error"
      : "warning";

  return (
    <Container
      data-testid={`suggestion-card-${s.patternId}`}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            <SpaceBetween size="xxs">
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Badge color={PRIORITY_COLOR[s.priority] ?? "grey"}>
                  {s.priority}
                </Badge>
                <Box variant="h3" display="inline">
                  {(fromRun && s.suggestedCategory?.trim()) ||
                    suggestionHeadline(s)}
                </Box>
                {earlier && (
                  <span
                    title={`This pattern is from TrackStars report ${earlier.reportId}, not the latest one. It stays listed because an open investigation was started from it.`}
                  >
                    <Badge color="grey">
                      Earlier report · {earlierReportLabel(earlier)}
                    </Badge>
                  </span>
                )}
              </SpaceBetween>
              <Box color="text-body-secondary" fontSize="body-s">
                {fromRun ? "" : `${s.totalMatches.toLocaleString()} items · `}
                <StatusIndicator type={confidenceType}>
                  {describeConfidence(s)}
                </StatusIndicator>
                {s.affectedQueues?.length && !s.derivedRule?.length
                  ? ` · ${s.affectedQueues.join(", ")}`
                  : ""}
                {relationNote ? ` · ${relationNote}` : ""}
              </Box>
            </SpaceBetween>
          </div>
          {!onInvestigate && headerAction}
          {onInvestigate &&
            (covered ? (
              // Already under investigation: the action is to go there, not to start another.
              <OpenInvestigationButton
                stage={stage}
                investigationId={covered.investigationId}
                title={covered.title}
                variant="normal"
              >
                Open investigation
              </OpenInvestigationButton>
            ) : held ? (
              // Most of it is already an open case: that is where the work is. Its owner can
              // widen the case's rule from the case's TrackStars tab.
              <OpenInvestigationButton
                stage={stage}
                investigationId={held.investigationId}
                title={held.title}
                variant="normal"
              >
                Open investigation
              </OpenInvestigationButton>
            ) : (
              <Button
                variant="primary"
                disabled={!canInvestigate}
                disabledReason="No population rule could be derived from this pattern"
                onClick={() => onInvestigate(s)}
              >
                Start investigation
              </Button>
            ))}
        </div>
      }
    >
      <SpaceBetween size="s">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            columnGap: "12px",
            rowGap: "6px",
            // Prose gets hard to read past ~80 characters a line on wide screens.
            maxWidth: READABLE_WIDTH,
          }}
        >
          <Box variant="awsui-key-label">Why</Box>
          <Box>
            {whyShown ? (
              <CodeProse text={whyShown} />
            ) : (
              "TrackStars gave no root cause."
            )}
            {whyLong && (
              <>
                {" "}
                <Button
                  variant="inline-link"
                  onClick={() => setWhyExpanded((v) => !v)}
                >
                  {whyExpanded ? "less" : "more"}
                </Button>
              </>
            )}
          </Box>
          <Box variant="awsui-key-label">Fix</Box>
          <Box>
            <CodeProse text={plainFix} />
          </Box>
          {shown && (
            <>
              <Box variant="awsui-key-label">
                {held?.unowned === 0
                  ? "Covered"
                  : held
                  ? "Mostly covered"
                  : s.ownership.state === "partial"
                  ? "Partly covered"
                  : "Already investigated"}
              </Box>
              <Box data-testid="suggestion-covered-by">
                {/* The row already describes the population; only who holds it is new information. */}
                {ownerLabel(shown.owner ?? null)}
                {shown.status ? ` · ${formatStatus(shown.status)}` : ""}
                {held && held.unowned > 0
                  ? ` · holds ${Math.round(held.share * 100)}%`
                  : ""}
                {s.ownership.investigations.length > 1
                  ? ` · +${s.ownership.investigations.length - 1} more`
                  : ""}
                {" · "}
                <OpenInvestigationButton
                  stage={stage}
                  investigationId={shown.investigationId}
                  title={shown.title}
                />
              </Box>
            </>
          )}
        </div>
        {actions}
        <ExpandableSection headerText="Details" variant="footer">
          <SpaceBetween size="s">
            <Box>
              <Box variant="awsui-key-label">TrackStars said</Box>
              <Box>
                <CodeProse text={s.description} />
              </Box>
              <Box color="text-body-secondary" fontSize="body-s">
                {evidenceSummary(s)} · {layerWord(s.rootCause?.layer)}
              </Box>
            </Box>
            {s.rootCause?.evidence?.length ? (
              <Box>
                <Box variant="awsui-key-label">Findings</Box>
                <ul style={{ margin: "4px 0 0 0" }}>
                  {s.rootCause.evidence.map((e) => (
                    <li key={e}>
                      <CodeProse text={e} />
                    </li>
                  ))}
                </ul>
              </Box>
            ) : null}
            {s.recommendedAction?.description && (
              <Box>
                <Box variant="awsui-key-label">Recommended action</Box>
                <Box>
                  <CodeProse text={s.recommendedAction.description} />
                </Box>
                {s.recommendedAction.suggestedCodeFix && (
                  <Box color="text-body-secondary" fontSize="body-s">
                    <CodeProse text={s.recommendedAction.suggestedCodeFix} />
                  </Box>
                )}
              </Box>
            )}
            {s.samples?.length ? (
              <Box>
                <Box variant="awsui-key-label">Sample transactions</Box>
                {s.samples.map((sample, i) => (
                  <React.Fragment
                    key={
                      sample.orderId ??
                      sample.transactionId ??
                      sample.errorMessage
                    }
                  >
                    {i > 0 ? ", " : ""}
                    <TransactionLink
                      stage={stage}
                      transactionId={sample.transactionId ?? undefined}
                      orderId={sample.orderId ?? undefined}
                    />
                  </React.Fragment>
                ))}
              </Box>
            ) : null}
            {s.derivedRule?.length ? (
              <Box>
                <Box variant="awsui-key-label">Population rule</Box>
                {s.derivedRule.map((p, i) => (
                  <React.Fragment key={p.field}>
                    {i > 0 ? " · " : ""}
                    <code>
                      {p.field}{" "}
                      {predicateValues(p).length > 1
                        ? `IN (${predicateValueText(p)})`
                        : `= ${predicateValueText(p)}`}
                    </code>
                  </React.Fragment>
                ))}
                {(s.constraints ?? [])
                  .flatMap((c) => [
                    ...(c.contains?.length
                      ? [`${c.field} contains ${c.contains.join(" and ")}`]
                      : []),
                    ...(c.excludes?.length
                      ? [
                          `${c.field} does not contain ${c.excludes.join(
                            " or "
                          )}`,
                        ]
                      : []),
                    ...(c.notIn?.length
                      ? [`${c.field} is not ${c.notIn.join(" or ")}`]
                      : []),
                  ])
                  .map((text) => (
                    <React.Fragment key={text}>
                      {" · "}
                      <code>{text}</code>
                    </React.Fragment>
                  ))}
              </Box>
            ) : (
              <Box color="text-status-warning">
                No population rule could be derived; start from a sample
                transaction instead.
              </Box>
            )}
            {(s.suggestedCategory || s.suggestedWhenClause) && (
              <Box>
                <Box variant="awsui-key-label">
                  Proposed error_category
                  {s.suggestedCategory ? `: ${s.suggestedCategory}` : ""}
                </Box>
                {s.suggestedWhenClause && (
                  <pre style={{ margin: "4px 0 0 0", whiteSpace: "pre-wrap" }}>
                    {s.suggestedWhenClause}
                  </pre>
                )}
              </Box>
            )}
            <Box color="text-body-secondary" fontSize="body-s">
              <span title={s.patternId}>
                {shortPatternId(s.patternId, suggestionReportId(s))}
              </span>
              {detailsReportUrl ? (
                <>
                  {" · "}
                  <Link href={detailsReportUrl} external>
                    Open in TrackStars
                  </Link>
                </>
              ) : null}
            </Box>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Container>
  );
};

export default TrackStarsSuggestionCard;
